import { ComponentProps, FC } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CalendarEvent,
  Widget,
  WidgetHighlightButton,
  WidgetSection,
} from '@factorialco/f0-react/dist/experimental'
import {
  AlertCircle,
  Completed,
  CrossedCircle,
  DottedCircle,
  InProgressTask,
  PartiallyCompleted,
} from '@factorialco/f0-react/icons/app'
import { TrainingsInsightDocument, TrainingsInsightQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { trainingSessionCalendarEventNormalizer } from 'modules/trainings/lib/calendarEvent/normalizer'
import { TRAINING_MEMBERSHIP_STATUS } from 'modules/trainings/constants/trainingLearner'

import './index.styles.css'
type Item = ComponentProps<typeof WidgetHighlightButton>

const prepareData = (data: TrainingsInsightQuery) => {
  const completedCount = data.trainings.trainingsConnection.nodes.filter(
    (training) => training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.COMPLETED
  ).length
  const notAssignedCount = data.trainings.trainingsConnection.nodes.filter(
    (training) => training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.NOT_ASSIGNED
  ).length
  const notStartedCount = data.trainings.trainingsConnection.nodes.filter(
    (training) => training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.NOT_STARTED
  ).length
  const startedCount = data.trainings.trainingsConnection.nodes.filter(
    (training) => training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.STARTED
  ).length
  const partiallyCompletedCount = data.trainings.trainingsConnection.nodes.filter(
    (training) =>
      training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.PARTIALLY_COMPLETED
  ).length
  const absentCount = data.trainings.trainingsConnection.nodes.filter(
    (training) => training.trainingAttendanceStatus === TRAINING_MEMBERSHIP_STATUS.MISSING
  ).length

  const items: Item[] = [
    {
      label: i18n.t('insights.widgets.trainings.not_assigned'),
      count: notAssignedCount,
      icon: AlertCircle,
      iconClassName: 'text-f1-icon-warning',
    },
    {
      label: i18n.t('insights.widgets.trainings.not_started'),
      count: notStartedCount,
      icon: DottedCircle,
      iconClassName: 'text-f1-icon-secondary',
    },
    {
      label: i18n.t('insights.widgets.trainings.started'),
      count: startedCount,
      icon: InProgressTask,
      iconClassName: 'text-f1-icon-info',
    },
    {
      label: i18n.t('insights.widgets.trainings.partially_completed'),
      count: partiallyCompletedCount,
      icon: PartiallyCompleted,
      iconClassName: 'text-f1-icon-warning',
    },
    {
      label: i18n.t('insights.widgets.trainings.completed'),
      count: completedCount,
      icon: Completed,
      iconClassName: 'text-f1-icon-positive',
    },
    {
      label: i18n.t('insights.widgets.trainings.absent'),
      count: absentCount,
      icon: CrossedCircle,
      iconClassName: 'text-f1-icon-critical',
    },
  ]

  const nextSessionData = data.trainings.sessionsConnection.nodes[0]

  const nextSession = nextSessionData
    ? trainingSessionCalendarEventNormalizer(nextSessionData)
    : undefined

  return {
    title: i18n.t('insights.widgets.trainings.title'),
    items,
    nextSession,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const TrainingsWidgetComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({ data: { title, items, nextSession }, onClickWidget, employeeId }) => {
  const { navigation } = useNavigation()

  const linkUrl = navigation.employees.id.training.fullPath({ id: employeeId })

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const link = {
    title: i18n.t('insights.widgets.trainings.title'),
    url: linkUrl,
  }

  return (
    <Widget
      header={{
        title,
        link,
      }}
    >
      {items.length && (
        <Box className={'Trainings_list'}>
          {items.map((item) => (
            <WidgetHighlightButton key={item.label} {...item} />
          ))}
        </Box>
      )}

      {nextSession && (
        <WidgetSection title={i18n.t('insights.widgets.trainings.next_session')}>
          <CalendarEvent {...nextSession} />
        </WidgetSection>
      )}
    </Widget>
  )
}

export const buildTrainingsWidget = () => {
  return buildWidget({
    id: 'trainings',
    document: TrainingsInsightDocument,
    prepareData,
    component: TrainingsWidgetComponent,
    policies: ['seeEmployeeTrainings'],
  })
}
